import {gql} from '@apollo/client';
import {DocumentNode} from 'graphql';

import FileBrowserActions from 'file-browser/FileBrowserActions';
import FolderSelectorModalClass from 'file-browser/FolderSelectorModal';
import {MixtureFileQuery} from 'folder-items/__generated/MixtureFileQuery';
import {FolderItemFileQueryVariables} from 'folder-items/ApolloFolderItemBase';
import ApolloRegistryItemBase from 'folder-items/ApolloRegistryItemBase';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import QueryWrapper from 'graphql-client/QueryWrapper';

import {InaccessibleInfoGraphQL} from '../../graphql-fragments/auth/__generated/InaccessibleInfoGraphQL';

type GraphQLMixtureFileData = ExtractByTypename<MixtureFileQuery['mixture'], 'MixtureNode'>;

type InstructionsGQL = NonNullable<GraphQLMixtureFileData['instructions']>;

// Query for mixture-specific attributes here even though they aren't used by the backbone model, in order to
// pre-populate the Apollo cache with these values so the IngredientTable component does not start with a
// loading state in the mixture tab.
export const MIXTURE_FILE_QUERY: DocumentNode = gql`
  query MixtureFileQuery($fileId: StrictString!) @engComponent(name: REGISTRY) {
    mixture(id: $fileId) @audit(msg: "Opened mixture {name}") {
      ... on MixtureNode {
        name
        ...BaseFolderItemFileGraphQL
        ...MixtureSpecificAttributesGraphQL
        instructions {
          ...MixtureInstructionsGraphQL
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('BaseFolderItemFile').fragment}
  ${getFragmentWrapper('MixtureSpecificAttributes').fragment}
  ${getFragmentWrapper('MixtureInstructions').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export default class Mixture extends ApolloRegistryItemBase<MixtureFileQuery, GraphQLMixtureFileData> {
  protected getQueryWrapper(): QueryWrapper<MixtureFileQuery, FolderItemFileQueryVariables> {
    return {query: MIXTURE_FILE_QUERY};
  }

  duplicate(): void {
    FolderSelectorModalClass.openForCopy(
      {
        showInventory: true,
        showNotebook: false,
      },
      (folderId) => {
        FileBrowserActions.copyAndOpenItem(this.id, folderId);
      }
    );
  }

  protected getGraphQLFolderItemFromQueryData(
    data: MixtureFileQuery
  ): GraphQLMixtureFileData | InaccessibleInfoGraphQL {
    return data.mixture;
  }

  protected getUpdatedQueryData(attrs: Partial<GraphQLMixtureFileData>): MixtureFileQuery {
    return {
      mixture: {
        ...this.folderItem(),
        ...attrs,
      },
    };
  }

  getInstructions(): InstructionsGQL | null {
    return this.folderItem().instructions;
  }
}
